import { AddAllLinks } from '../AddAllLinks/AddAllLinks'
import { AddSection } from '../AddSection/AddSection'
import { AddWidgetType } from '@app/page/src/components/widgets'
import { AddWidgets } from '../../../Links/AddNew'
import { AnimatePresence, m as motion } from 'framer-motion'
import { Box } from '@dtx-company/design-system/src'
import { ContactInfoFormV2 } from '../../../../../components/widgets/Contact/ContactForm'
import { DesignMenu } from '../DesignMenu/DesignMenu'
import { FC, MouseEvent, PropsWithChildren, useState } from 'react'
import { LinkEditDrawerContent } from './LinkEditDrawer.constants'
import {
  LinkProvider,
  SocialLinkType,
  socialLinks
} from '@dtx-company/inter-app/src/constants/linkTypes'
import { LinkType } from '@dtx-company/flow-codegen/src/page/typeUtils'
import { Form as SocialLinkForm } from '../../../../widgets/SocialLink'
import { StandardLinkForm } from '../../../../../components/widgets/Destination/DestinationForm'
import { SwipeableDrawer } from '@mui/material'
import { WidgetDrawerForm } from '../AddAllLinks/WidgetDrawerForm/WidgetDrawerForm'
import { animationProps, desktopAnimationProps } from './LinkEditDrawer.utils'
import { useLinkEditDrawerState } from './LinkEditDrawer.hooks'
import { useNextLinkOrder } from '../../../../../hooks/useGetLinkOrder'
import { useScreenSizeBreakpoints } from '../../../../../../../common/src/hooks/useScreenSizeBreakpoints'
import noop from 'lodash/noop'
import styled from 'styled-components'

const StyledMotionDiv = styled(motion.div)`
  width: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 16px;
`

const ResponsiveStyledMotionDiv: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { isTabletOrDesktop } = useScreenSizeBreakpoints()
  return (
    <StyledMotionDiv {...(isTabletOrDesktop ? desktopAnimationProps : animationProps)}>
      {children}
    </StyledMotionDiv>
  )
}

export const DrawerComponent: FC<{
  type: LinkEditDrawerContent | null
  currentEditLink: LinkType | null
  handleClose: () => void
  onClickAddWidget?: (link: AddWidgetType | SocialLinkType) => void
  height?: string
}> = ({ type, currentEditLink, handleClose, onClickAddWidget, height }) => {
  const order = useNextLinkOrder()
  const linkOrder = currentEditLink?.order ?? order

  switch (type) {
    case LinkEditDrawerContent.ADD_LINK:
      return (
        <AnimatePresence initial={false}>
          <ResponsiveStyledMotionDiv>
            <AddAllLinks />
          </ResponsiveStyledMotionDiv>
        </AnimatePresence>
      )
    case LinkEditDrawerContent.CONTACT_INFO:
      return (
        <AnimatePresence initial={false}>
          <ResponsiveStyledMotionDiv>
            <ContactInfoFormV2
              handleClose={handleClose}
              order={linkOrder}
              curr={currentEditLink || undefined}
              widgetObj={{
                provider: LinkProvider.WIDGET,
                type: 'contactInfo',
                id: 'contactInfo'
              }}
            />
          </ResponsiveStyledMotionDiv>
        </AnimatePresence>
      )
    case LinkEditDrawerContent.ADD_SECTION:
      return (
        <AnimatePresence initial={false}>
          <ResponsiveStyledMotionDiv>
            <AddSection />
          </ResponsiveStyledMotionDiv>
        </AnimatePresence>
      )
    case LinkEditDrawerContent.DESIGN:
      return <DesignMenu height={height} />
    case LinkEditDrawerContent.DESTINATION:
      return (
        <AnimatePresence initial={false}>
          <ResponsiveStyledMotionDiv>
            <StandardLinkForm
              curr={currentEditLink || undefined}
              widgetObj={socialLinks[0]}
              order={linkOrder}
              handleClose={handleClose}
            />
          </ResponsiveStyledMotionDiv>
        </AnimatePresence>
      )
    case LinkEditDrawerContent.ADD_WIDGET:
      return (
        <AnimatePresence>
          <ResponsiveStyledMotionDiv>
            <AddWidgets onClick={onClickAddWidget || noop} handleClose={handleClose} />
          </ResponsiveStyledMotionDiv>
        </AnimatePresence>
      )
    case LinkEditDrawerContent.SOCIALS:
      return (
        <AnimatePresence>
          <ResponsiveStyledMotionDiv>
            <SocialLinkForm
              handleClose={handleClose}
              curr={currentEditLink || undefined}
              order={linkOrder}
              widgetObj={{
                provider: LinkProvider.WIDGET,
                type: 'socialLink',
                id: 'socialLink'
              }}
            />
          </ResponsiveStyledMotionDiv>
        </AnimatePresence>
      )
    case LinkEditDrawerContent.WIDGET_FORM:
      return (
        <AnimatePresence>
          <ResponsiveStyledMotionDiv>
            <WidgetDrawerForm handleClose={handleClose} order={linkOrder} />
          </ResponsiveStyledMotionDiv>
        </AnimatePresence>
      )
    default:
      return <></>
  }
}

export const LinkEditDrawer: FC = () => {
  const [touchStart, setTouchStart] = useState<number | undefined>(undefined)
  const [touchEnd, setTouchEnd] = useState<number | undefined>(undefined)
  const [height, setHeight] = useState('366px')
  const handleTouchStart = (clientY: number): void => {
    setTouchStart(clientY)
  }

  const handleTouchMove = (clientY: number): void => {
    setTouchEnd(clientY)
  }

  const handleMouseDown = (e: MouseEvent): void => {
    e.preventDefault()
    handleTouchStart(e.clientY)
  }
  const handleMouseEnd = (e: MouseEvent): void => {
    e.preventDefault()
    handleTouchMove(e.clientY)
  }

  const handleTouchEnd = (): void => {
    if (!touchStart || !touchEnd) return
    if (touchStart - touchEnd > 50) {
      setHeight('100vh')
    }
    if (touchStart - touchEnd < -50) {
      setHeight('366px')
    }
  }
  const {
    onDrawerClose,
    onDrawerOpen,
    drawerOpen,
    drawerContentState,
    previewLink,
    onClickAddWidget
  } = useLinkEditDrawerState()

  return (
    <SwipeableDrawer
      anchor="bottom"
      onClose={onDrawerClose}
      onOpen={onDrawerOpen}
      open={drawerOpen}
      hideBackdrop
    >
      <Box
        onTouchStart={e => handleTouchStart(e.touches[0].clientY)}
        onTouchEnd={handleTouchEnd}
        onTouchMove={e => handleTouchMove(e.touches[0].clientY)}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseEnd}
        data-testid="link-edit-swipeable-drawer"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          transition: `${height} 0.5s`,
          height
        }}
      >
        <Box
          id="puller"
          sx={theme => ({
            width: theme.spacing(7.5),
            height: theme.spacing(1.5),
            borderRadius: theme.spacing(1),
            top: theme.spacing(2),
            left: `calc(50% - ${theme.spacing(4)})`,
            position: 'absolute',
            backgroundColor: 'primary.main',
            zIndex: 10
          })}
        />
        <DrawerComponent
          handleClose={() => {
            setHeight('366px')
            onDrawerClose()
          }}
          type={drawerContentState}
          height={height}
          currentEditLink={previewLink}
          onClickAddWidget={onClickAddWidget}
        />
      </Box>
    </SwipeableDrawer>
  )
}
